import Logo from "@/ui/components/Logo";
import ThinkingAnimation from "@/ui/components/ThinkingAnimation";
import { useState, useEffect } from "preact/hooks";
import { toTitleCase } from "@/lib/strings";
import { getLocalizationForManifest, postFeedback } from "@/lib/api_controller";
import { getEffectiveUiLanguage, uiLangIsRtl } from "@/l11n/l11n";

const MAX_CONTENT_WIDTH = "360px";

const ActivityFeedbackPartial = () => {
  const [feedbackItems, setFeedbackItems] = useState({});
  const [showThanks, setShowThanks] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [l11n, setL11n] = useState({});
  const [activityId, setActivityId] = useState(null);

  const uiIsRtl = uiLangIsRtl();
  const uiLang = getEffectiveUiLanguage();

  // MARK: onLoad
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Get Activity ID From URL
    const activityIdParam = params.get("aid");
    if (activityIdParam == null) {
      console.error("No activity ID provided. Using null.");
    } else {
      setActivityId(activityIdParam);
    }

    // Fetch the localization
    async function fetchLocalization() {
      let localization = await getLocalizationForManifest(page_localization_manifest);
      if (!localization) {
        console.error("Failed to fetch localization. Using English fallback.");
        localization = fallback_localization_en;
      }
      console.log(`Localization:`, localization);
      setL11n(localization);
      setIsLoading(false);
    }
    fetchLocalization();
  }, []);

  // MARK: Memoized Rating Protos
  const difficultyRatingProto = {
    id: "difficulty",
    title: l11n.rating_difficulty_title,
    description: l11n.rating_difficulty_description,
    scaleIndex: 2,
    units: [
      { title: l11n.rating_too_easy, value: 0 },
      { title: l11n.rating_just_right, value: 2 },
      { title: l11n.rating_too_hard, value: 1 },
    ],
  };

  // MARK: Bugs
  const bugsProto = {
    id: "bugs",
    title: l11n.ci_q_bug_title,
    description: l11n.ci_q_bug,
    ext: l11n.ci_p_bug_ext,
    ext_condition: 0,
    scaleIndex: 1,
    units: [
      { title: l11n.rating_yes, value: 0, emoji: "🐛" },
      { title: l11n.rating_no, value: 1, emoji: "👍" },
    ],
  };

  // MARK: L11N
  const l11nProto = {
    id: "l11n",
    title: l11n.ci_q_localization_title,
    description: l11n.ci_q_localization_quality,
    scaleIndex: 2,
    units: [
      { title: l11n.rating_poor, value: 0, emoji: "👎" },
      { title: l11n.rating_okay, value: 1, emoji: "" },
      { title: l11n.rating_good, value: 2, emoji: "👍" },
    ],
  };

  // MARK: Another Activity
  const anotherActivityProto = {
    id: "another_activity",
    title: l11n.rating_another_activity_title,
    description: l11n.rating_another_activity_description,
    scaleIndex: 2,
    units: [
      { title: l11n.rating_no, value: 0, emoji: "👎" },
      { title: l11n.rating_maybe, value: 1, emoji: "🤷‍♂️" },
      { title: l11n.rating_definitely, value: 2, emoji: "👍" },
    ],
  };

  // MARK: Overall Rtng
  const taaliaOverallRatingProto = {
    id: "taalia_overall_rating",
    title: l11n.rating_overall_taalia_title,
    description: l11n.rating_overall_taalia_prompt,
    scaleIndex: 4,
    units: [
      { value: 0, label: l11n.rating_very_bad, emoji: "😖" },
      { value: 1, label: l11n.rating_poor, emoji: "🙁" },
      { value: 2, label: l11n.rating_okay, emoji: "😐" },
      { value: 3, label: l11n.rating_good, emoji: "🙂" },
      { value: 4, label: l11n.rating_great, emoji: "😄" },
    ],
  };

  // MARK: Set Feedback
  async function setFeedbackItem(id, value) {
    console.log("setting feedback item", id, value);
    const updatedFeedbackItems = { ...feedbackItems, [id]: value };
    setFeedbackItems(updatedFeedbackItems);
    const feedbackData = {
      type: "activity-partial",
      activityId,
      items: updatedFeedbackItems,
    };
    console.log("posting feedback", feedbackData);
    postFeedback(feedbackData);
  }

  // MARK: Submit Feedback
  async function submitFeedback() {
    // Get the feedback text from the textarea
    const feedbackText = document.querySelector("textarea").value;
    if (feedbackText.length > 0) {
      setFeedbackItem("feedbackText", feedbackText);
    }

    // Show the thank you message
    setShowThanks(true);
  }

  // MARK: Early Render
  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <ThinkingAnimation />
      </div>
    );
  } else if (activityId == null) {
    return (
      <div
        className="text-2xl font-semibold text-gray-800 mb-4 break-words max-w-[380px] mx-auto"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        No activity ID provided.
      </div>
    );
  }

  // MARK: Render
  return (
    <>
      <div className={`flex flex-col min-h-screen space-y-4 ${uiIsRtl ? "rtl" : "ltr"}`}>
        {/* Logo */}
        <div className="mt-8">
          <Logo />
        </div>

        {/* Activity Header */}
        <div
          className={`w-full max-w-[${MAX_CONTENT_WIDTH}] py-2 px-2 text-left mx-auto`}
        >
          <div
            className={`text-[clamp(1rem,3.5vw,1.25rem)] font-normal text-gray-800 break-words w-full italic px-4 ${
              uiIsRtl ? "text-right" : "text-left"
            }`}
          >
            {l11n.ci_incomplete_activity_header}
          </div>
        </div>

        {/* Difficulty Rating */}
        <div
          className={`w-full max-w-[${MAX_CONTENT_WIDTH}] mx-auto my-4 py-4 px-2 text-center bg-gray-50 rounded-xl`}
        >
          <ThreePointRow
            item={difficultyRatingProto}
            selectedItem={feedbackItems.difficulty}
            setFeedbackItemCallback={setFeedbackItem}
            uiIsRtl={uiIsRtl}
          />
        </div>

        {/* Bugs */}
        <div
          className={`w-full max-w-[${MAX_CONTENT_WIDTH}] mx-auto my-4 py-4 px-2 text-center bg-gray-50 rounded-xl`}
        >
          <ThreePointRow
            item={bugsProto}
            selectedItem={feedbackItems.bugs}
            setFeedbackItemCallback={setFeedbackItem}
            uiIsRtl={uiIsRtl}
          />
        </div>

        {/* L11N */}
        {uiLang !== "EN" && (
          <div
            className={`w-full max-w-[${MAX_CONTENT_WIDTH}] mx-auto my-4 py-4 px-2 text-center bg-gray-50 rounded-xl`}
          >
            <ThreePointRow
              item={l11nProto}
              selectedItem={feedbackItems.l11n}
              setFeedbackItemCallback={setFeedbackItem}
              uiIsRtl={uiIsRtl}
            />
          </div>
        )}

        {/* Another Activity */}
        <div
          className={`w-full max-w-[${MAX_CONTENT_WIDTH}] mx-auto my-4 py-4 px-2 text-center bg-gray-50 rounded-xl`}
        >
          <ThreePointRow
            item={anotherActivityProto}
            selectedItem={feedbackItems.another_activity}
            setFeedbackItemCallback={setFeedbackItem}
            uiIsRtl={uiIsRtl}
          />
        </div>

        {/* Taalia Overall Rating */}
        <div
          className={`w-full max-w-[${MAX_CONTENT_WIDTH}] mx-auto my-4 py-4 px-2 text-center bg-gray-50 rounded-xl`}
        >
          <TaaliaOverallRatingRow
            item={taaliaOverallRatingProto}
            selectedItem={feedbackItems.taalia_overall_rating}
            setFeedbackItemCallback={setFeedbackItem}
            uiIsRtl={uiIsRtl}
          />
        </div>

        {/* Additional Feedback */}
        <div
          className={`w-full max-w-[${MAX_CONTENT_WIDTH}] mx-auto my-4 py-4 px-2 text-center bg-gray-50 rounded-xl`}
        >
          <AdditionalFeedbackRow
            l11n={l11n}
            setFeedbackItemCallback={setFeedbackItem}
            uiIsRtl={uiIsRtl}
          />
        </div>

        {/* Submit Button */}
        <div className={`w-full max-w-[${MAX_CONTENT_WIDTH}] mx-auto mb-8 px-10`}>
          <button
            className="w-full py-3 px-4 mb-8 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-[clamp(0.875rem,2.5vw,1rem)]"
            onClick={() => submitFeedback()}
          >
            {l11n.rating_submit}
          </button>
        </div>
      </div>

      {showThanks && (
        <div className="fixed inset-0 bg-white flex items-center justify-center">
          <div className="text-[clamp(1.5rem,5vw,2rem)] font-semibold text-gray-800 text-center">
            {l11n.rating_thank_you}
          </div>
        </div>
      )}
    </>
  );
};

export default ActivityFeedbackPartial;

// MARK: 3 Point Row
const ThreePointRow = ({ item, selectedItem, setFeedbackItemCallback, uiIsRtl }) => {
  console.log("ThreePointRow selectedItem", selectedItem);
  console.log("ThreePointRow item", item);
  return (
    <div className="w-full flex flex-col items-center">
      <div
        className={`text-[clamp(1.25rem,4vw,1.5rem)] font-semibold text-gray-800 mb-3 break-words w-full text-center px-2`}
        style={{
          direction: uiIsRtl ? "rtl" : "ltr",
        }}
      >
        {item.title.toUpperCase()}
      </div>
      <div
        className={`text-[clamp(1rem,3.5vw,1.25rem)] font-medium text-gray-800 mb-4 px-2 break-words w-full text-center`}
        style={{
          direction: uiIsRtl ? "rtl" : "ltr",
        }}
      >
        {item.description}
      </div>
      <div
        className={`w-full flex ${
          uiIsRtl ? "flex-row-reverse" : "flex-row"
        } justify-center items-center gap-2`}
      >
        {item.units.map((unit) => (
          <div
            key={unit.value}
            onClick={() =>
              setFeedbackItemCallback(item.id, {
                value: unit.value,
                scaleIndex: item.scaleIndex,
                percentage: (unit.value / item.scaleIndex) * 100,
              })
            }
            className={`flex flex-col items-center justify-center cursor-pointer p-2 rounded-lg transition-all duration-200 border border-transparent hover:border-gray-300 w-[100px] ${
              selectedItem?.value === unit.value
                ? "!bg-[#b5d8ff] scale-105"
                : "bg-gray-100"
            }`}
          >
            <div className="text-[clamp(0.875rem,2.5vw,1rem)] font-semibold text-gray-600 min-h-[2.5rem] text-center w-full flex items-center justify-center">
              {toTitleCase(unit.title)}
            </div>
            <div className="text-[clamp(1.5rem,4vw,1.75rem)] text-center w-full items-center justify-center">
              {unit.emoji ||
                Array(parseInt(unit.value) + 1)
                  .fill("⭐️")
                  .join("")}
            </div>
          </div>
        ))}
      </div>
      {item.ext && selectedItem?.value === item.ext_condition && (
        <div
          className={`text-[clamp(1rem,3.5vw,1.25rem)] font-normal text-gray-800 mb-0 mt-6 px-2 break-words w-full ${
            uiIsRtl ? "text-right" : "text-left"
          }`}
        >
          {item.ext}
        </div>
      )}
    </div>
  );
};

// MARK: Addtl Feedback
const AdditionalFeedbackRow = ({ l11n, uiIsRtl }) => {
  return (
    <>
      <div
        className={`text-[clamp(1.25rem,4vw,1.5rem)] font-semibold text-gray-800 mb-4 break-words max-w-[${MAX_CONTENT_WIDTH}] mx-auto text-center px-2`}
        style={{
          direction: uiIsRtl ? "rtl" : "ltr",
        }}
      >
        {l11n.rating_prompt_additional_title.toUpperCase()}
      </div>
      <div
        className={`text-[clamp(1rem,3.5vw,1.25rem)] font-normal text-gray-800 mb-4 px-2 break-words max-w-[${MAX_CONTENT_WIDTH}]`}
        style={{
          direction: uiIsRtl ? "rtl" : "ltr",
        }}
      >
        {l11n.ci_p_changes_another_chance}
      </div>
      <div>
        <textarea
          className={`w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-[clamp(0.875rem,2.5vw,1rem)] ${
            uiIsRtl ? "text-right" : "text-left"
          }`}
          placeholder={l11n.rating_prompt_additional_placeholder}
          dir={uiIsRtl ? "rtl" : "ltr"}
        ></textarea>
      </div>
    </>
  );
};

// MARK: Taalia Overall Rating
const TaaliaOverallRatingRow = ({
  item,
  selectedItem,
  setFeedbackItemCallback,
  uiIsRtl,
}) => {
  return (
    <>
      <div
        className={`text-[clamp(1.25rem,4vw,1.5rem)] font-semibold text-gray-800 mb-4 break-words max-w-[${MAX_CONTENT_WIDTH}] mx-auto text-center px-2`}
        style={{
          direction: uiIsRtl ? "rtl" : "ltr",
        }}
      >
        {item.title.toUpperCase()}
      </div>
      <div
        className={`text-[clamp(1rem,3.5vw,1.25rem)] font-normal text-gray-800 mb-4 px-2 break-words max-w-[${MAX_CONTENT_WIDTH}] text-center`}
        style={{
          direction: uiIsRtl ? "rtl" : "ltr",
        }}
      >
        {item.description}
      </div>
      <div
        className={`flex ${
          uiIsRtl ? "flex-row-reverse" : "flex-row"
        } justify-between items-center m-0 p-0 w-full max-w-[${MAX_CONTENT_WIDTH}] mx-auto space-x-1`}
      >
        {item.units.map(({ value, label, emoji }) => (
          <div
            key={value}
            onClick={() =>
              setFeedbackItemCallback(item.id, {
                value,
                scaleIndex: item.scaleIndex,
                percentage: (value / item.scaleIndex) * 100,
              })
            }
            className={`flex flex-col items-center justify-center cursor-pointer p-2 rounded-lg transition-all duration-200 border border-transparent hover:border-gray-300 flex-1 min-w-0 ${
              selectedItem?.value === value ? "!bg-[#b5d8ff] scale-105" : "bg-gray-100"
            }`}
          >
            <div className="h-8 mb-2 flex items-center">
              <span className="text-[clamp(2rem,6vw,2.5rem)]">{emoji}</span>
            </div>
            <span className="text-[clamp(0.875rem,2.5vw,1rem)] font-semibold text-gray-600 min-h-[2.5rem] text-center w-full flex items-center justify-center">
              {toTitleCase(label)}
            </span>
          </div>
        ))}
      </div>
    </>
  );
};

const page_localization_manifest = [
  "rating_overall_taalia_title",
  "rating_overall_taalia_prompt",
  "ci_incomplete_activity_header",
  "ci_q_bug",
  "ci_q_bug_title",
  "ci_p_bug_ext",
  "ci_q_localization_quality",
  "ci_q_localization_title",
  "ci_p_changes_another_chance",
  "rating_another_activity_title",
  "rating_another_activity_description",
  "rating_prompt_additional_title",
  "rating_difficulty_title",
  "rating_difficulty_description",
  "rating_thank_you",
  "rating_submit",
  "rating_prompt_additional_placeholder",
  "rating_yes",
  "rating_no",
  "rating_maybe",
  "rating_definitely",

  // maybe:
  "rating_very_bad",
  "rating_poor",
  "rating_okay",
  "rating_good",
  "rating_great",
  "rating_too_short",
  "rating_just_right",
  "rating_too_long",
  "rating_too_easy",
  "rating_too_hard",
  "rating_not_useful",
  "rating_very_useful",
];

const fallback_localization_en = {
  rating_overall_taalia_title: "Taalia Overall Rating",
  rating_overall_taalia_prompt: "How would you rate your overall experience with Taalia?",
  ci_incomplete_activity_header:
    "It looks like you started an activity with Taalia but did not complete it. We're really interested in understanding why.",
  ci_q_bug: "Did you encounter a bug?",
  ci_q_bug_title: "Bug",
  ci_p_bug_ext:
    "Please tell us what you saw in the text field below or email us team@taalia.com",
  ci_q_localization_quality: "Was the {{scriptLangName}} ok?",
  ci_q_localization_title: "Localization",
  rating_prompt_additional_title: "General Feedback",
  ci_p_changes_another_chance:
    "What can we change about Taalia that would make you give it another chance?",
  rating_another_activity_title: "ANOTHER ACTIVITY",
  rating_another_activity_description:
    "Are you interested in doing more activities like this one with Taalia?",
  rating_difficulty_title: "DIFFICULTY",
  rating_difficulty_description: "How difficult was the activity?",
  rating_thank_you: "Thanks for the feedback!",
  rating_submit: "Submit",
  rating_prompt_additional_placeholder: "Type your message here...",
  rating_yes: "Yes",
  rating_no: "No",
  rating_maybe: "Maybe",
  rating_definitely: "Definitely",

  // Maybe:
  rating_very_bad: "very bad",
  rating_poor: "poor",
  rating_okay: "okay",
  rating_good: "good",
  rating_great: "great",
  rating_too_short: "Too Short",
  rating_just_right: "Just Right",
  rating_too_long: "Too Long",
  rating_too_easy: "Too Easy",
  rating_too_hard: "Too Hard",
  rating_not_useful: "Not Useful",
  rating_very_useful: "Very Useful",

  //   rating_length_title: "LENGTH",
  //   rating_length_description: "How long was the activity?",
};

//   // Process rating parameter after activityId is set
//   useEffect(() => {
//     if (activityId) {
//       const params = new URLSearchParams(window.location.search);
//       const ratingParam = params.get("overall_rating");
//       if (ratingParam !== null) {
//         const rating = parseInt(ratingParam, 10);
//         if (!isNaN(rating) && rating >= 0 && rating <= 4) {
//           console.log("setting overall_rating", rating);
//           setFeedbackItem("overall_rating", {
//             value: rating,
//             scaleIndex: 4,
//             percentage: (rating / 4) * 100,
//           });
//         }
//       }
//     }
//   }, [activityId]);
